// Pick a service address for a customer.
//
// This replaced a bare <select> fed only by `?unassigned=1`. Two problems with
// that: an address already serving someone else simply wasn't in the list (with
// no hint that it existed), and a 135-entry dropdown of raw address strings is
// not something you can find anything in.
//
// NO TYPE GATE. Until 2026-08-04 this bucketed any address whose type differed from the
// customer's into a blocked "wrong type" group, telling the operator to create a separate
// entry. That was right while a customer was one type — and wrong from the moment Kevin
// decided a customer may hold both business and residential lines. The server's gate went
// then; this one survived and kept refusing the very arrangement the model now exists for
// (a home business at one street address).
//
// The type still matters — but between the LINE'S PACKAGE and the address, which is what
// Commio actually compares (a business CNAM over a residential 911 site is a 412). That
// check lives in `createLine`, where the package is known. A customer having addresses of
// both types is not a problem; selling a business package against the residential one is,
// and that is caught where it happens.
//
// So: type to filter, results grouped by whether the address is free or already
// in use, and — the important part — addresses that CAN'T be used are still
// shown, greyed, with the reason on the row. An option that silently isn't there
// sends someone hunting through the Locations page to work out why.

import { useMemo, useState } from 'react';

const TYPE_LABEL = { business: 'Business', residential: 'Residential' };

function TypeTag({ type }) {
  const cls = type === 'business' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex flex-shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium ${cls}`}>
      {TYPE_LABEL[type] || type || '—'}
    </span>
  );
}

function fullAddress(l) {
  const line1 = l.addressLine1 || l.address_line1 || l.label || `Location #${l.id}`;
  const line2 = l.addressLine2 || l.address_line2;
  const tail = [[l.city, l.state].filter(Boolean).join(', '), l.postal || l.zip].filter(Boolean).join(' ');
  return { line1: [line1, line2].filter(Boolean).join(', '), tail };
}

function haystack(l) {
  return [
    l.label, l.addressLine1 || l.address_line1, l.addressLine2 || l.address_line2,
    l.city, l.state, l.postal || l.zip, l.type,
    ...(Array.isArray(l.subscribers) ? l.subscribers.map((s) => s.name) : []),
  ].filter(Boolean).join(' ').toLowerCase();
}

// No `blocked` state any more: every address in the org is selectable. The retired
// wrong-type gate lived here, and leaving the branch behind would invite somebody to wire
// the rule back up.
function Row({ loc, count, selected, onPick }) {
  const { line1, tail } = fullAddress(loc);
  const names = Array.isArray(loc.subscribers) ? loc.subscribers.map((s) => s.name).filter(Boolean) : [];
  return (
    <button
      type="button"
      onClick={() => onPick(loc.id)}
      className={`flex w-full items-start gap-2 border-l-2 px-3 py-2 text-left transition-colors ${
        selected ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:bg-gray-50'
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium text-gray-900">{line1}</span>
          <TypeTag type={loc.type} />
          {count > 0 && (
            <span className="inline-flex flex-shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
              {count} here
            </span>
          )}
        </span>
        {tail && <span className="mt-0.5 block text-xs text-gray-500">{tail}</span>}
        {names.length > 0 && (
          <span className="mt-0.5 block truncate text-xs text-gray-500">Also serves {names.join(', ')}</span>
        )}
      </span>
      {selected && <span className="mt-0.5 flex-shrink-0 text-sm font-bold text-blue-600">✓</span>}
    </button>
  );
}

function Group({ title, hint, rows, value, onPick }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <div className="sticky top-0 flex items-baseline justify-between gap-2 border-y border-gray-200 bg-gray-50 px-3 py-1">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{title}</span>
        <span className="text-[11px] text-gray-400">{rows.length}</span>
      </div>
      {hint && <p className="px-3 pt-1.5 text-xs text-gray-500">{hint}</p>}
      {rows.map((r) => (
        <Row key={r.loc.id} {...r} onPick={onPick} selected={Number(value) === Number(r.loc.id)} />
      ))}
    </div>
  );
}

/**
 * @param locations   every location in the org (with subscribers[] / subscriberCount)
 * @param subscriber  { id, name, type } — the customer being assigned to
 * @param assignedIds ids this customer already has
 */
export default function LocationAssignPicker({
  locations, subscriber, assignedIds, isLoading,
  value, onChange, onSubmit, onCancel, onCreateNew, isPending, error,
}) {
  const [q, setQ] = useState('');
  const mine = useMemo(() => new Set((assignedIds || []).map(Number)), [assignedIds]);

  const count = (l) => {
    if (l.subscriberCount != null) return Number(l.subscriberCount);
    if (Array.isArray(l.subscribers)) return l.subscribers.length;
    return (l.subscriber?.id ?? l.subscriberId ?? l.subscriber_id) != null ? 1 : 0;
  };

  const { free, inUse } = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = { free: [], inUse: [] };
    for (const loc of locations || []) {
      if (mine.has(Number(loc.id))) continue;                    // already theirs
      if (needle && !haystack(loc).includes(needle)) continue;
      const n = count(loc);
      // Grouped by whether anyone is there yet, and nothing else. Every address in this
      // org is selectable: a customer may hold business and residential addresses at
      // once, which is how a home business is modelled.
      (n === 0 ? out.free : out.inUse).push({ loc, count: n });
    }
    return out;
  }, [locations, q, mine]);

  const total = free.length + inUse.length;

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-xs font-medium text-gray-700">
          Service address for {subscriber?.name || 'this customer'}
        </label>
        {onCreateNew && (
          <button type="button" onClick={onCreateNew}
                  className="text-xs font-medium text-blue-600 hover:underline">
            + Create a new address
          </button>
        )}
      </div>

      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by street, city, ZIP, or the customer already there…"
        className="mt-1.5 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />

      <div className="mt-2 max-h-80 overflow-y-auto rounded-md border border-gray-200 bg-white">
        {isLoading ? (
          <p className="px-3 py-6 text-center text-sm text-gray-500">Loading addresses…</p>
        ) : total === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-gray-500">
            {q.trim()
              ? <>No address matches &ldquo;{q.trim()}&rdquo;.</>
              : 'No other addresses on file yet.'}
          </p>
        ) : (
          <>
            <Group title="Not in use" rows={free} value={value} onPick={onChange} />
            <Group
              title="Already serving another customer"
              hint="Sharing an address is fine — each customer keeps their own numbers, extensions and devices. Only the dispatch address is shared."
              rows={inUse} value={value} onPick={onChange}
            />
          </>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!value || isPending}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isPending ? 'Assigning…' : 'Assign address'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
